/* eslint-disable @typescript-eslint/no-non-null-assertion */
/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Apr 17 2023
 **/

import React, { useEffect, useId, useRef, forwardRef, useMemo, useState } from "react";

import { filterHTMLAttribute } from "@/frameworks/libraries/utilities";

import { bodyCss, headerCss, groupCss, wrapCss, dragWrapCss } from "./private/emotion";
import useFoldingB from "./useFoldingB";
import { Divider } from "../../Divider";

type DefaultData = {
	isOpen: boolean;
	height: `${number}px` | string;
	upToTop: number;
	draggable?: boolean;
};

export interface FoldingBProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactElement<FoldingChildProps & { ref: React.RefObject<HTMLDivElement> }>[];
	defaultData?: Partial<DefaultData>;
	["data-id"]?: string;
	onChangeHeight?: (height: string) => void;
}

export interface FoldingChildProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
	["data-id"]?: string;
	isOpen?: boolean;
}

const baseDefaultData = {
	isOpen: true,
	draggable: false,
	//height means the height of the folding B body
	height: "100px",
	upToTop: 60
} as DefaultData;

const SELF_CLOSING_BORDER = 10;

/**
 * @description
 * 개발자 테스트 필수 확인
 * 1. 새로고침 UI 이상유무
 * 2. 새로고침 후 overflow 스크롤 정상 유무
 * 3. flip-flop 기능, 정상유무
 * 4. drag 구분선 동작
 * 5. 브라우저 확대 축소
 */
function FoldingB({ defaultData, onChangeHeight, ...props }: FoldingBProps) {
	const data = useMemo(
		() => ({
			...baseDefaultData,
			...defaultData
		}),
		[defaultData]
	);
	const { state, handlers } = useFoldingB();
	const id = props["data-id"] || useId();
	const ref = useRef<HTMLDivElement>(null);
	const headRef = useRef<HTMLDivElement>(null);
	const bodyRef = useRef<HTMLDivElement>(null);
	const [isMouseDown, setIsMouseDown] = useState(false);
	const startHeight = useRef(0);
	const startY = useRef(0);
	const dragEl = useRef<HTMLDivElement>(null);

	const handleResize = () => {
		if (
			!ref.current ||
			!bodyRef.current ||
			!headRef.current ||
			!ref.current.previousElementSibling ||
			!ref.current.parentElement
		)
			return;
		const parentElement = ref.current.parentElement as HTMLDivElement;
		let newBodyHeight = bodyRef.current.offsetHeight;
		// console.debug("handleResize1", parentElement, newBodyHeight);

		if (ref.current.offsetHeight > parentElement.offsetHeight) {
			newBodyHeight = parentElement.offsetHeight - headRef.current.offsetHeight;
		}
		if (newBodyHeight < 0) {
			newBodyHeight = 0;
		}
		// console.debug("handleResize2", parentElement, newBodyHeight);
		handleUpdateHeight(newBodyHeight);
	};

	const handleMouseDown = (evt: MouseEvent) => {
		evt.preventDefault();
		const { clientY } = evt;
		startY.current = clientY - (headRef.current?.offsetHeight || 0);
		const wrap = headRef.current?.parentElement as HTMLDivElement;
		startHeight.current = wrap.offsetHeight;
		setIsMouseDown(true);
	};

	const updatePrevHeight = (height: number) => {
		if (!ref.current?.previousElementSibling) return;
		const prevSiblingEl = ref.current?.previousElementSibling as HTMLDivElement;
		const prevSiblingHeaderEl = prevSiblingEl.querySelector("[data-type=fold-header]") as HTMLDivElement;
		const prevSiblingBodyEl = prevSiblingEl.querySelector("[data-type=fold-body]") as HTMLDivElement;
		if (!prevSiblingEl.dataset?.["isopen"]) return;
		if (!prevSiblingHeaderEl) return;
		// console.debug(
		// 	"folding test",
		// 	ref.current.parentElement,
		// 	prevSiblingHeaderEl,
		// 	prevSiblingBodyEl,
		// 	prevSiblingHeaderEl.offsetHeight,
		// 	prevSiblingHeaderEl.clientHeight,
		// 	height
		// );
		prevSiblingBodyEl.style.height =
			ref.current.parentElement!.offsetHeight - prevSiblingHeaderEl.offsetHeight - height + "px";
	};

	const handleMouseMove = (evt: MouseEvent) => {
		if (!isMouseDown || !ref.current || !headRef.current || !bodyRef.current) return;

		if (evt.clientY < data.upToTop!) return;
		const newHeight = startHeight.current + (startY.current - evt.clientY);

		if (newHeight <= headRef.current.offsetHeight + SELF_CLOSING_BORDER || newHeight < 0) {
			state[id]?.isOpen && handlers.handleHide(id, "system");
			return;
		} else {
			!state[id]?.isOpen && handlers.handleShow(id, "system");
		}

		handleUpdateHeight(newHeight);
		bodyRef.current.style.height = `${newHeight}px`;
		onChangeHeight?.(`${newHeight}px`);
		updatePrevHeight(newHeight + headRef.current.offsetHeight);
	};

	const handleUpdateHeight = (bodyHeight: number) => {
		if (!ref.current || !headRef.current || !bodyRef.current) return;
		bodyRef.current.style.height = `${bodyHeight}px`;
		onChangeHeight?.(`${bodyHeight}px`);
		updatePrevHeight(bodyHeight + headRef.current.offsetHeight);
	};

	const stopDragging = () => {
		setIsMouseDown(false);
		document.documentElement.style.userSelect = "initial";
	};

	if (!state) return null;
	useEffect(() => {
		handlers.handleRegister(id, { ...defaultData, ref, bodyRef, headRef });
		() => {
			handlers.handleUnregister(id);
		};
	}, []);

	useEffect(() => {
		if (!bodyRef.current || !headRef.current) return;
		if (state[id]?.isOpen) {
			bodyRef.current.style.height = data.height;
			updatePrevHeight(parseFloat(data.height) + headRef.current?.offsetHeight);
		} else {
			bodyRef.current.style.height = `${0}px`;
			updatePrevHeight(headRef.current?.offsetHeight || 0);
		}

		return () => {
			const prevID = handlers.getPrevID(id);
			if (prevID) {
				const element = state[prevID].ref?.current;
				if (element) {
					element.style.height = "100%";
				}
			}
		};
	}, [state[id]?.isOpen]);

	useEffect(() => {
		if (!data.draggable) return;
		window.addEventListener("resize", handleResize);
		document.addEventListener("mousemove", handleMouseMove, { passive: true });
		document.addEventListener("mouseup", stopDragging);

		if (defaultData?.draggable) {
			dragEl?.current?.addEventListener("mousedown", handleMouseDown);
		}

		return () => {
			window.addEventListener("resize", handleResize);
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", stopDragging);

			if (defaultData?.draggable) {
				dragEl?.current?.removeEventListener("mousedown", handleMouseDown);
			}
		};
	}, [ref, isMouseDown]);

	return (
		<div css={[groupCss(isMouseDown)]} data-id={id} data-isopen={data.isOpen} {...props} role="group" ref={ref}>
			{defaultData?.draggable && (
				<Divider
					ref={dragEl}
					type="drag"
					css={{
						position: "absolute",
						top: "-4px"
					}}
				/>
			)}
			{/* Wrap */}
			<div css={[dragWrapCss(Boolean(defaultData?.draggable))]}>
				{/* Header */}
				{props.children[0] &&
					React.isValidElement(props.children[0]) &&
					React.cloneElement(props.children[0], {
						["data-id"]: id,
						isOpen: state[id]?.isOpen || false,
						ref: headRef
					})}
				{/* Body*/}
				{props.children[1] &&
					React.isValidElement(props.children[1]) &&
					React.cloneElement(props.children[1], {
						["data-id"]: id,
						isOpen: state[id]?.isOpen || false,
						ref: bodyRef
					})}
			</div>
		</div>
	);
}

const Header = forwardRef((props: FoldingChildProps, ref: React.ForwardedRef<HTMLDivElement>) => {
	return (
		<div
			css={[headerCss()]}
			{...filterHTMLAttribute(props)}
			ref={ref}
			onClick={props.onClick}
			data-type="fold-header"
		/>
	);
});

const Wrap = forwardRef((props: FoldingChildProps, ref: React.ForwardedRef<HTMLDivElement>) => {
	return <div ref={ref} css={[wrapCss()]} {...filterHTMLAttribute(props)} data-type="fold-wrap" />;
});

const Body = forwardRef((props: FoldingChildProps, ref: React.ForwardedRef<HTMLDivElement>) => {
	return <div ref={ref} css={[bodyCss()]} {...filterHTMLAttribute(props)} data-type="fold-body" />;
});

FoldingB.Wrap = Wrap;
FoldingB.Header = Header;
FoldingB.Body = Body;

export default FoldingB;
